import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, Search, ScanLine } from "lucide-react";
import Seal from "@/components/Seal";
import { toast } from "sonner";

export default function Verify() {
  const params = useParams();
  const [number, setNumber] = useState(params.credentialNumber || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async (n = number) => {
    if (!n) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get(`/registry/verify-full/${encodeURIComponent(n.trim())}`);
      setResult(data);
    } catch (e) {
      setError(errMessage(e));
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.credentialNumber) verify(params.credentialNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.credentialNumber]);

  return (
    <div data-testid="verify-page">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Credential verification</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-primary tracking-tight">
            Verify a credential.
          </h1>
          <p className="mt-3 text-slate-600">
            Enter a credential number (e.g. <span className="font-mono-data text-primary">ACIS-2025-01001</span>) to
            confirm authenticity, current status, and scope of practice.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-6 border-slate-200 shadow-sm">
            <label className="text-sm font-medium text-slate-700 mb-2 block">Credential number</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="verify-input"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && verify()}
                  placeholder="ACIS-2025-01001"
                  className="pl-9 font-mono-data"
                />
              </div>
              <Button data-testid="verify-btn" onClick={() => verify()} className="bg-primary text-white hover:bg-[#113A64]">
                <Search className="h-4 w-4 mr-2" />
                Verify
              </Button>
            </div>
          </Card>

          {loading && (
            <div className="mt-6 text-sm text-slate-500">Looking up credential…</div>
          )}

          {error && !loading && (
            <Card className="mt-6 p-6 border-red-200 bg-red-50/50">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-red-600" />
                <div>
                  <div className="font-heading font-semibold text-red-700">Credential not found</div>
                  <div className="text-sm text-slate-700 mt-1">{error}. Double-check the credential number or search the public registry.</div>
                </div>
              </div>
            </Card>
          )}

          {result && !loading && (
            <Card className="mt-6 p-7 border-slate-200 shadow-sm" data-testid="verify-result">
              <div className="flex items-center justify-between mb-5">
                <div className="inline-flex items-center gap-2 text-emerald-700">
                  <ShieldCheck className="h-5 w-5" />
                  <span className="text-xs uppercase tracking-[0.2em] font-bold">Verified — Status on file</span>
                </div>
                <Badge className="capitalize bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100">{result.credential.status}</Badge>
              </div>
              <div className="flex items-start gap-5">
                <Seal credentialType={result.credential.credential_type} size="lg" showLabel />
                <div className="flex-1">
                  <div className="font-heading text-2xl font-bold text-primary" data-testid="verify-holder-name">{result.credential.holder_name}</div>
                  <div className="text-sm text-slate-600 mt-1">{result.credential.credential_type}{result.credential.state ? ` · ${result.credential.state}` : ""}</div>
                </div>
              </div>

              <div className="border-t border-slate-200 mt-5 pt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <Field label="Credential number" value={result.credential.credential_number} mono />
                <Field label="Issuing board" value={result.credential.organization} />
                <Field label="Issue date" value={formatDate(result.credential.issue_date)} />
                <Field label="Expires" value={formatDate(result.credential.expiry_date)} />
              </div>

              {result.credential.school?.id && (
                <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-5">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Graduating institution</div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-heading font-semibold text-primary">{result.credential.school.name}</div>
                      <div className="text-xs text-slate-500 font-mono-data mt-0.5">{result.credential.school.accreditation_number}</div>
                    </div>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 capitalize">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      ACIS · {result.credential.school.accreditation_status}
                    </Badge>
                  </div>
                </div>
              )}

              {result.licenses?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Licenses</div>
                  <div className="space-y-2">
                    {result.licenses.map((l, i) => (
                      <div key={i} className="rounded-md border border-slate-200 bg-white p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`verify-license-${i}`}>
                        <div className="flex items-center gap-3">
                          {l.license_number?.startsWith("LHT-") && <Seal kind="ibthm_licensed" size="sm" />}
                          <div>
                            <span className="font-mono-data text-sm text-primary font-semibold">{l.license_number}</span>
                            <span className="text-xs text-slate-500 ml-2">· {l.jurisdiction || "—"}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {l.dependency_display ? (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[11px]">
                              {l.dependency_display}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className={`capitalize text-[11px] ${
                              l.status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                              l.status === "suspended" ? "bg-amber-50 text-amber-800 border-amber-200" :
                              l.status === "revoked" ? "bg-red-50 text-red-800 border-red-200" :
                              "bg-slate-50 text-slate-700 border-slate-200"
                            }`}>
                              {l.status}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.all_credentials?.length > 1 && (
                <div className="mt-5" data-testid="verify-all-creds">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">All credentials held</div>
                  <div className="flex flex-wrap gap-2">
                    {result.all_credentials.map((c) => (
                      <Badge key={c.credential_number} variant="outline" className={`text-[11px] ${
                        c.status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                        c.status === "suspended" ? "bg-amber-50 text-amber-800 border-amber-200" :
                        c.status === "revoked" ? "bg-red-50 text-red-800 border-red-200" :
                        "bg-slate-50 text-slate-700 border-slate-200"
                      }`}>
                        {c.credential_type} · {c.credential_number} · {c.status}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {result.disciplinary?.length > 0 && (
                <div className="mt-5">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-amber-700 mb-2">Disciplinary on file</div>
                  <div className="space-y-2">
                    {result.disciplinary.map((d, i) => (
                      <div key={i} className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] font-bold text-amber-700">
                          <span>{d.status.replace(/_/g, " ")} · {d.severity}</span>
                          <span>{new Date(d.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="mt-1">{d.public_summary}</div>
                        {d.sanction && <div className="text-xs italic mt-1">Sanction: {d.sanction}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className={`text-sm text-slate-800 mt-1 ${mono ? "font-mono-data text-primary font-semibold" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}
